import React, { lazy, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useCoachExperience, isCoachRequestCancelled } from '../context/CoachExperienceContext';
import { coachStartAPI } from '../services/api';
import RouteSuspense from './RouteSuspense';
import BrandLogo from './BrandLogo';
import './CoachStart.css';

const CoachStartPage = lazy(() => import('./CoachStartPage'));
const bypass = path => /^\/(invite|parent-invite|oauth-consent|verify-email|reset-password|pending-approval|privacy-policy|terms-of-service)(\/|$)/.test(path);

export default function CoachEntryGate({ user, onLogout, children }) {
  const location = useLocation();
  const experience = useCoachExperience();
  const [useLegacy, setUseLegacy] = useState(false);
  const exposed = useRef('');
  const gateHeading = useRef(null);
  const isCoach = ['coach', 'club_admin'].includes(user?.role);
  const home = ['/', '/dashboard'].includes(location.pathname);
  const entryPath = home || location.pathname === '/coach-start' || (!user?.hasClub && location.pathname !== '/club-setup');
  const { data, loading, error, accountChanged, unsupported, run, refresh, ownerId } = experience;
  const holdingEntry = (loading && !data && entryPath) || accountChanged || (error && entryPath && !useLegacy);

  useEffect(() => { setUseLegacy(false); exposed.current = ''; }, [ownerId]);
  useEffect(() => {
    if (holdingEntry) gateHeading.current?.focus();
  }, [holdingEntry, error, accountChanged, ownerId]);
  useEffect(() => {
    const exposureKey = `${ownerId}:${data?.cohort}`;
    if (!data?.cohort || !entryPath || bypass(location.pathname) || exposed.current === exposureKey
        || (data.cohort === 'beta_self_service' && location.pathname !== '/coach-start')) return;
    exposed.current = exposureKey;
    run(options => coachStartAPI.exposure(options)).catch(err => {
      if (!isCoachRequestCancelled(err)) console.warn('[CoachStart] entry acknowledgement unavailable');
    });
  }, [data?.cohort, entryPath, location.pathname, ownerId, run]);

  if (!user || !isCoach || bypass(location.pathname)) return children;
  if (unsupported && !accountChanged) {
    if (location.pathname === '/coach-start') return <Navigate to="/" replace />;
    return <><p role="status">{error}</p>{children}</>;
  }
  if (holdingEntry) {
    return <main className="coach-start-shell">
      <section className="coach-start-panel">
        <BrandLogo height={38} showBeta />
        <h1 tabIndex={-1} ref={gateHeading}>{accountChanged ? 'Your sign-in changed' : error ? 'Your coaching setup could not be loaded' : 'Your coaching setup'}</h1>
        {loading && !accountChanged ? <p role="status">Checking your coaching setup...</p> : <>
          <p role="alert">{error}</p>
          <div className="coach-start-actions">
            <button type="button" onClick={accountChanged ? () => window.location.reload() : refresh}>Try again</button>
            {!accountChanged && <button type="button" className="secondary" onClick={() => setUseLegacy(true)}>Continue with the normal app</button>}
            <button type="button" className="secondary" onClick={onLogout}>Sign out</button>
          </div>
        </>}
      </section>
    </main>;
  }
  if (data?.entry === 'starter' && entryPath && location.pathname !== '/coach-start' && !useLegacy) {
    return <Navigate to="/coach-start" replace />;
  }
  if (location.pathname === '/coach-start') {
    if (useLegacy || !data?.flags?.starter) return <Navigate to="/" replace />;
    return <RouteSuspense><CoachStartPage onLogout={onLogout} /></RouteSuspense>;
  }
  return children;
}
